/**
 * Client-side Cal.com booking config. T-077, ARCH-011.
 *
 * The Cal.com embed runs in the subscriber's browser; the team slug and the
 * two event-type slugs are public data (the embed makes them visible to
 * anyone who opens the page). Reading them from `import.meta.env.VITE_CAL_*`
 * mirrors the Stripe-publishable-key precedent and keeps `CAL_API_KEY`
 * server-side.
 *
 * The server-side mirror of this config lives in
 * `packages/studio-mcp/src/booking/index.ts` per ARCH-011 §8; the type alias
 * `CalcomEventTypeName` is duplicated here to avoid a portal -> studio-mcp
 * runtime import (studio-mcp is the server/MCP package; the portal client
 * bundle should not pull it in). The two surfaces stay in lockstep by
 * convention; archgate `ARCH-011/embed-only-on-booking-route` (planned, see
 * ADR §Compliance) will enforce the import boundary once the rule arms.
 */

export type CalcomEventTypeName = 'pro-monthly-call' | 'pro-bookable-hour';

interface BrowserBookingEnv {
  teamSlug: string;
  proMonthlyCallSlug: string;
  proBookableHourSlug: string;
}

/**
 * Read the public booking env from `import.meta.env`. Throws if any required
 * variable is missing so the misconfiguration surfaces during dev/build
 * rather than as a silently-broken embed.
 */
export function readBrowserBookingEnv(): BrowserBookingEnv {
  const env = import.meta.env as Record<string, string | undefined>;
  const required: Array<[keyof BrowserBookingEnv, string]> = [
    ['teamSlug', 'VITE_CAL_TEAM_SLUG'],
    ['proMonthlyCallSlug', 'VITE_CAL_EVENT_TYPE_PRO_MONTHLY_CALL'],
    ['proBookableHourSlug', 'VITE_CAL_EVENT_TYPE_PRO_BOOKABLE_HOUR'],
  ];

  const partial: Partial<BrowserBookingEnv> = {};
  for (const [key, varName] of required) {
    const value = env[varName];
    if (!value || value.trim().length === 0) {
      throw new Error(
        `[booking] Missing required Vite env ${varName}. ` +
          `See app/docs/required-secrets.md.`,
      );
    }
    partial[key] = value.trim();
  }
  return partial as BrowserBookingEnv;
}

/**
 * Returns `<team-slug>/<event-type-slug>` for the named event type. This is
 * the exact string the `@calcom/embed-react` `<Cal>` component consumes as
 * its `calLink` prop.
 */
export function calLinkForEventType(name: CalcomEventTypeName): string {
  const env = readBrowserBookingEnv();
  switch (name) {
    case 'pro-monthly-call':
      return `${env.teamSlug}/${env.proMonthlyCallSlug}`;
    case 'pro-bookable-hour':
      return `${env.teamSlug}/${env.proBookableHourSlug}`;
    default: {
      const exhaustive: never = name;
      throw new Error(`[booking] No slug configured for event type ${exhaustive}`);
    }
  }
}

/**
 * Resolves a CSS custom property to its computed value at runtime. The Cal.com
 * embed's `cssVarsPerTheme` config expects literal hex / rgb / px values,
 * not `var(--token)` references, so the design-token map gets resolved on
 * the document root before being handed to `cal('ui', ...)`.
 */
function resolveToken(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value.length > 0 ? value : fallback;
}

/**
 * The CSS-variable map handed to `cal('ui', { cssVarsPerTheme: { light: ... } })`.
 * Cal.com's embed uses its own token names (`--cal-bg`, `--cal-brand`, etc.);
 * this map projects the portal's design-token values onto those names so the
 * embed visually belongs to the portal (linen surface, Oxford Navy text,
 * Cotton Rose brand accent).
 *
 * Resolved at call time, not at module load, so the values reflect any
 * CUSTOMIZE updates to `@studio/design-tokens` without a portal rebuild.
 */
export function resolveBookingCssVars(): Record<string, string> {
  return {
    '--cal-brand': resolveToken('--color-rose', '#e8b5bc'),
    '--cal-brand-emphasis': resolveToken('--color-navy', '#0b1b2b'),
    '--cal-brand-text': resolveToken('--color-navy', '#0b1b2b'),
    '--cal-bg': resolveToken('--color-linen', '#eee7dc'),
    '--cal-bg-emphasis': resolveToken('--color-white', '#faf7f2'),
    '--cal-bg-muted': resolveToken('--color-white', '#faf7f2'),
    '--cal-text': resolveToken('--color-navy', '#0b1b2b'),
    '--cal-text-emphasis': resolveToken('--color-navy', '#0b1b2b'),
    '--cal-text-muted': resolveToken('--color-text-mute', '#5e6a76'),
    '--cal-border': resolveToken('--color-rule', 'rgba(11, 27, 43, 0.12)'),
    '--cal-border-subtle': resolveToken(
      '--color-rule',
      'rgba(11, 27, 43, 0.12)',
    ),
    '--cal-font-regular': resolveToken(
      '--font-body',
      "'Work Sans', system-ui, sans-serif",
    ),
    '--cal-font-cal': resolveToken(
      '--font-display',
      "'Instrument Serif', Georgia, serif",
    ),
  };
}
